import { FC, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";
import styled from "styled-components";

import { CollapseGroup } from "capitalrx-components-2.0/lib/general";
import { Grid, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Paragraph } from "capitalrx-components-2.0/lib/typography";

import { EnrollmentPlanCSID } from "@/api/types";
import { PlanCard } from "@/premium/pages/PlanSelect/PlanCard";
import { Card, ContentContainer, ThemedTitle } from "@/premium/themedComponents";
import { ThemedH5 } from "@/premium/themedComponents/Headers";
import { EnrollmentContext } from "@/state";
import { ANALYTIC_EVENTS, logEvent } from "@/utils/analytics";

const ThemedBanner = styled.div`
  border-bottom: solid 8px ${(props) => props.theme.primary_color};
`;

const BannerGrid = styled(Grid)`
  column-gap: 0rem;
  row-gap: 0rem;
`;

const HeaderImageHorizontal = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const HeaderImageVertical = styled.img`
  min-width: 100%;
`;

const ImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  height: 100%;
`;

export const ThemedH2 = styled.h2`
  font-family: ${(props) => props.theme.font || "Source Sans Pro"};
  font-weight: 400;
  font-size: 28px;
  color: #313e48;
  padding-top: 80px;
  padding-bottom: 25px;
`;

export const StepParagraph = styled(Markdown)`
  font-family: Source Sans Pro;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0px;
  text-align: center !important;
`;

export const StepContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

export const ThemedBannerParagraph = styled(Paragraph)`
  font-family: Source Sans Pro;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0px;
  text-align: left;
`;

export const Gradient = styled.div<{ $maxBannerHeight?: string }>`
  background-image: linear-gradient(to left, #989898, #4a4a4a);
  padding: 15% 20%;
  color: white;
  height: 100%;
`;

export const Question = styled.div`
  font-family: Source Sans Pro;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0px;
  text-align: left;
  text-transform: none;
  padding-left: 5px;
`;

export const Answer = styled.div`
  font-family: Source Sans Pro;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0px;
  text-align: left;
`;

export const PlanSelect: FC = () => {
  const { t: translate, i18n } = useTranslation();
  const [state, _] = useContext(EnrollmentContext);
  const { isLoading } = state;
  const theme = state.experienceData.theme;
  const plans = state.experienceData.plan_csids;
  const faqs = state.experienceData.faqs;
  const [expandedFaqs, setExpandedFaqs] = useState<ReadonlyArray<string>>([]);

  const { headerColumns, enrollmentJourneyColumns, verticalHeader } = useResponsive(
    useMemo(
      () => ({
        headerColumns: { desktop: 6, tablet: 6, mobile: 12 },
        enrollmentJourneyColumns: { desktop: 4, tablet: 4, mobile: 12 },
        verticalHeader: { desktop: false, tablet: false, mobile: true },
      }),
      [],
    ),
  );

  const Header = useMemo(() => {
    if (verticalHeader) {
      return (
        <BannerGrid spacing="none">
          <Grid.Span columns={headerColumns}>
            <ThemedBanner>
              <ImageContainer>
                <HeaderImageVertical src={theme.home_image} alt={theme.home_image_alt_text} />
              </ImageContainer>
            </ThemedBanner>
          </Grid.Span>
          <Grid.Span columns={headerColumns}>
            <Gradient>
              {theme.header_primary_text && <ThemedTitle>{theme.header_primary_text}</ThemedTitle>}
              {theme.header_secondary_text && <ThemedBannerParagraph>{theme.header_secondary_text}</ThemedBannerParagraph>}
            </Gradient>
          </Grid.Span>
        </BannerGrid>
      );
    } else {
      return (
        <ThemedBanner>
          <BannerGrid spacing="none">
            <Grid.Span columns={headerColumns - 1}>
              <Gradient>
                {theme.header_primary_text && <ThemedTitle>{theme.header_primary_text}</ThemedTitle>}
                {theme.header_secondary_text && <ThemedBannerParagraph>{theme.header_secondary_text}</ThemedBannerParagraph>}
              </Gradient>
            </Grid.Span>
            <Grid.Span columns={headerColumns + 1}>
              <ImageContainer>
                <HeaderImageHorizontal src={theme.home_image} alt={theme.home_image_alt_text} />
              </ImageContainer>
            </Grid.Span>
          </BannerGrid>
        </ThemedBanner>
      );
    }
  }, [verticalHeader, headerColumns, theme]);

  const Plans = useMemo(() => {
    if (isLoading) {
      return <div>{translate("PLAN_SELECT.planSelect.loadingPlans")}</div>;
    } else {
      return (
        <>
          <ThemedH2>{translate("PLAN_SELECT.planSelect.selectPlanToLearnMore")}</ThemedH2>
          <Grid>
            {plans.map((plan: EnrollmentPlanCSID, idx: number) => {
              return (
                <Grid.Span columns={headerColumns} key={idx}>
                  <PlanCard id={plan.id}>{plan.display_name}</PlanCard>
                </Grid.Span>
              );
            })}
          </Grid>
        </>
      );
    }
  }, [plans, headerColumns, isLoading, translate, i18n.language]);

  const Faqs = useMemo(() => {
    if (isLoading) {
      return <div>{translate("PLAN_SELECT.planSelect.loadingFaqs")}</div>;
    } else {
      return (
        faqs.length > 0 && (
          <>
            <ThemedH2>{translate("PLAN_SELECT.planSelect.frequentlyAskedQuestions")}</ThemedH2>
            <CollapseGroup alignCaret="left" spacing="none" expandedIds={expandedFaqs} onExpandedIdsChange={setExpandedFaqs}>
              {faqs.map((faq, idx) => {
                return (
                  <CollapseGroup.Collapse id={`faq-cg-${idx}`} key={idx} title={<Question>{faq.question}</Question>}>
                    <Answer>
                      <Markdown>{faq.content}</Markdown>
                    </Answer>
                  </CollapseGroup.Collapse>
                );
              })}
            </CollapseGroup>
          </>
        )
      );
    }
  }, [faqs, expandedFaqs, isLoading, translate, i18n.language]);

  const EnrollmentJourney = useMemo(
    () => (
      <>
        <ThemedH2>{translate("PLAN_SELECT.planSelect.yourEnrollmentJourney")}</ThemedH2>
        <Grid>
          <Grid.Span columns={enrollmentJourneyColumns}>
            <Card>
              <Stack>
                <img src={theme.step_01_image} alt={theme.step_01_image_alt_text} />
                <StepContainer>
                  <ThemedH5 $border={true}>{translate("PLAN_SELECT.planSelect.stepOne")}:</ThemedH5>
                </StepContainer>
                <StepParagraph>{theme.step_01_image_caption}</StepParagraph>
              </Stack>
            </Card>
          </Grid.Span>
          <Grid.Span columns={enrollmentJourneyColumns}>
            <Card fullHeight={true}>
              <Stack>
                <img src={theme.step_02_image} alt={theme.step_02_image_alt_text} />
                <StepContainer>
                  <ThemedH5>{translate("PLAN_SELECT.planSelect.stepTwo")}:</ThemedH5>
                </StepContainer>
                <StepParagraph>{theme.step_02_image_caption}</StepParagraph>
              </Stack>
            </Card>
          </Grid.Span>
          <Grid.Span columns={enrollmentJourneyColumns}>
            <Card fullHeight={true}>
              <Stack>
                <img src={theme.step_03_image} alt={theme.step_03_image_alt_text} />
                <StepContainer>
                  <ThemedH5>{translate("PLAN_SELECT.planSelect.stepThree")}:</ThemedH5>
                </StepContainer>
                <StepParagraph>{theme.step_03_image_caption}</StepParagraph>
              </Stack>
            </Card>
          </Grid.Span>
        </Grid>
      </>
    ),
    [enrollmentJourneyColumns, translate, i18n.language, theme],
  );

  useEffect(() => {
    logEvent(ANALYTIC_EVENTS.LANDING_PAGE);
  }, []);

  return (
    <ContentContainer>
      {Header}
      <Grid>
        <Grid.Span columns={1} />
        <Grid.Span columns={10}>
          <>
            {Plans}
            {Faqs}
            {theme.show_enrollment_journey && EnrollmentJourney}
          </>
        </Grid.Span>
        <Grid.Span columns={1} />
      </Grid>
    </ContentContainer>
  );
};
